import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { setAuthToken } from "../../services/authStore";

// Access token kept in module memory — never in localStorage (XSS protection).
// The refresh token lives in an HttpOnly cookie managed by the browser.
let _accessToken = null;

export const getAccessToken = () => _accessToken;

const setAxiosToken = (token) => {
	_accessToken = token;
	setAuthToken(token); // sync to authStore so socket-io can read it
	api.defaults.headers.Authorization = token ? `Bearer ${token}` : undefined;
};

const useAuth = () => {
	const navigate = useNavigate();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	// Register interceptors once on mount — never re-register on re-render
	useEffect(() => {
		const requestInterceptor = api.interceptors.request.use(
			config => {
				if (_accessToken) {
					config.headers["Authorization"] = `Bearer ${_accessToken}`;
				}
				return config;
			},
			error => Promise.reject(error)
		);

		// Mutex: garante que apenas UM refresh acontece por vez.
		// Múltiplas chamadas 401 simultâneas (ex: F5) aguardam o mesmo refresh.
		let refreshPromise = null;

		const responseInterceptor = api.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config;
				// 401 = sem token (race no carregamento inicial) ou token inválido
				// 403 = token expirado
				// Ambos: tentar renovar via cookie HttpOnly antes de redirecionar
				const isAuthError = error?.response?.status === 401 || error?.response?.status === 403;
				const isRefreshRequest = originalRequest.url?.includes("/auth/refresh_token");
				if (isAuthError && !originalRequest._retry && !isRefreshRequest) {
					originalRequest._retry = true;
					try {
						// Se já há um refresh em andamento, reutiliza a mesma Promise
						// em vez de disparar múltiplos requests simultâneos
						if (!refreshPromise) {
							refreshPromise = api.post("/auth/refresh_token").finally(() => {
								refreshPromise = null;
							});
						}
						const { data } = await refreshPromise;
						if (data) {
							setAxiosToken(data.token);
							setIsAuth(true);
							setUser(data.user);
						}
						return api(originalRequest);
					} catch (refreshError) {
						setAxiosToken(null);
						setIsAuth(false);
						navigate("/login");
						return Promise.reject(refreshError);
					}
				}
				return Promise.reject(error);
			}
		);

		// On page load: attempt silent refresh via HttpOnly cookie
		api.post("/auth/refresh_token")
			.then(({ data }) => {
				setAxiosToken(data.token);
				setIsAuth(true);
				setUser(data.user);
			})
			.catch(() => {
				navigate("/login");
			})
			.finally(() => setLoading(false));

		return () => {
			api.interceptors.request.eject(requestInterceptor);
			api.interceptors.response.eject(responseInterceptor);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		// Don't connect socket before authentication — token would be null
		if (!isAuth || !user?.id) return;

		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.off("user");
			socket.disconnect();
		};
	}, [isAuth, user.id]); // reconnect when auth state or user changes

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			setAxiosToken(data.token);
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			navigate("/tickets");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
		} catch (err) {
			toastError(err);
		} finally {
			setAxiosToken(null);
			setIsAuth(false);
			setUser({});
			setLoading(false);
			navigate("/login");
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
